import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { serviceService } from "../services/serviceService";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import {
  Image as ImageIcon,
  Video as VideoIcon,
  Trash2 as TrashIcon,
  ShieldCheckIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  Phone,
  MessageCircle,
} from "lucide-react";
import { BuildingOfficeIcon } from "@heroicons/react/24/outline";
import { baseURL } from "../../../lib/axios";
import toast from "react-hot-toast";

// Ligne clé/valeur compacte : libellé collé à la valeur, séparés par ":".
// `full` étale la ligne sur toute la largeur (adresse, email...).
// `stacked` repasse au libellé au-dessus de la valeur — réservé à la
// description, trop longue pour tenir sur une seule ligne.
const Field = ({ label, value, full = false, stacked = false }) => {
  if (value === null || value === undefined || value === "") return null;

  if (stacked) {
    return (
      <div className={full ? "col-span-2" : ""}>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm text-gray-900 font-medium whitespace-pre-line mt-0.5">
          {value}
        </p>
      </div>
    );
  }

  return (
    <div className={full ? "col-span-2" : ""}>
      <span className="text-xs text-gray-500">{label} : </span>
      <span className="text-sm text-gray-900 font-medium">{value}</span>
    </div>
  );
};

// Ligne téléphone avec deux actions directes (appeler / WhatsApp) au lieu
// d'afficher le numéro WhatsApp comme une simple ligne de texte séparée.
const PhoneField = ({ label, phone, whatsapp }) => {
  if (!phone) return null;
  const waNumber = (whatsapp || phone).replace(/\D/g, "");

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 shrink-0">{label} :</span>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-900 font-medium">{phone}</span>
        <a
          href={`tel:${phone.replace(/\s+/g, "")}`}
          title="Appeler"
          className="p-1.5 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100"
        >
          <Phone className="w-3.5 h-3.5" />
        </a>
        <a
          href={`https://wa.me/${waNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          title="WhatsApp"
          className="p-1.5 rounded-full bg-green-50 text-green-600 hover:bg-green-100"
        >
          <MessageCircle className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
};

// `price_range` peut être une chaîne, un tableau ou un objet {min, max}
// selon comment il a été saisi — gérer les trois pour ne jamais planter.
const formatPriceRange = (priceRange) => {
  if (!priceRange) return null;
  if (typeof priceRange === "string") return priceRange;
  if (Array.isArray(priceRange)) return priceRange.join(" - ");
  if (typeof priceRange === "object") {
    const { min, max } = priceRange;
    if (min && max) return `${min} - ${max} FCFA`;
    if (min) return `À partir de ${min} FCFA`;
  }
  return null;
};

const ServiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // États pour les modales
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [actionReason, setActionReason] = useState("");

  // Récupération des détails du service (Admin)
  const {
    data: service,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["serviceDetails", id],
    queryFn: () => serviceService.getAdminServiceDetails(id),
  });

  // Mutations
  const approveMutation = useMutation({
    mutationFn: (id) => serviceService.approveService(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["serviceDetails", id]);
      toast.success("Service approuvé avec succès");
    },
    onError: (error) =>
      toast.error(
        error.response?.data?.message || "Erreur lors de l'approbation"
      ),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }) => serviceService.rejectService(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries(["serviceDetails", id]);
      toast.success("Service rejeté avec succès");
      setShowRejectModal(false);
      setActionReason("");
    },
    onError: (error) =>
      toast.error(error.response?.data?.message || "Erreur lors du rejet"),
  });

  const suspendMutation = useMutation({
    mutationFn: ({ id, reason }) => serviceService.suspendService(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries(["serviceDetails", id]);
      toast.success("Service suspendu avec succès");
      setShowSuspendModal(false);
      setActionReason("");
    },
    onError: (error) =>
      toast.error(
        error.response?.data?.message || "Erreur lors de la suspension"
      ),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id) => serviceService.reactivateService(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["serviceDetails", id]);
      toast.success("Service réactivé avec succès");
    },
    onError: (error) =>
      toast.error(
        error.response?.data?.message || "Erreur lors de la réactivation"
      ),
  });

  const toggleVerifiedMutation = useMutation({
    mutationFn: (id) => serviceService.toggleVerified(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["serviceDetails", id]);
      toast.success("Statut vérifié mis à jour");
    },
    onError: () => toast.error("Erreur lors de la mise à jour"),
  });

  const deleteVideoMutation = useMutation({
    mutationFn: ({ id, index }) => serviceService.deleteServiceVideo(id, index),
    onSuccess: () => {
      queryClient.invalidateQueries(["serviceDetails", id]);
      toast.success("Vidéo supprimée");
    },
    onError: (error) =>
      toast.error(
        error.response?.data?.message || "Erreur lors de la suppression de la vidéo"
      ),
  });

  const handleDeleteVideo = (index) => {
    if (window.confirm("Supprimer définitivement cette vidéo ?")) {
      deleteVideoMutation.mutate({ id, index });
    }
  };

  // Gestionnaires d'actions
  const handleApprove = () => {
    if (window.confirm("Êtes-vous sûr de vouloir approuver ce service ?")) {
      approveMutation.mutate(id);
    }
  };

  const handleReject = () => {
    if (!actionReason.trim()) {
      toast.error("Veuillez fournir une raison pour le rejet");
      return;
    }
    rejectMutation.mutate({ id, reason: actionReason });
  };

  const handleSuspend = () => {
    if (!actionReason.trim()) {
      toast.error("Veuillez fournir une raison pour la suspension");
      return;
    }
    suspendMutation.mutate({ id, reason: actionReason });
  };

  const handleReactivate = () => {
    if (window.confirm("Êtes-vous sûr de vouloir réactiver ce service ?")) {
      reactivateMutation.mutate(id);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError)
    return (
      <div className="p-4 text-center">
        <div className="text-red-500 mb-4">
          Erreur lors du chargement du service.
        </div>
        <button
          onClick={() => navigate(-1)}
          className="text-blue-600 hover:underline"
        >
          Retour
        </button>
      </div>
    );

  return (
    <div className=" mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* En-tête et Actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          {service.logo ? (
            <img
              src={`${baseURL}/storage/${service.logo}`}
              alt="logo"
              className="h-20 w-20 rounded-full object-cover border-2 border-gray-100"
            />
          ) : (
            <div className="h-20 w-20 bg-primary/10 rounded-full flex items-center justify-center">
              <BuildingOfficeIcon className="h-10 w-10 text-primary" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {service.title}
            </h1>
            <p className="text-gray-500">{service.category}</p>
            <div className="mt-2 flex items-center gap-2">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  service.status === "approved"
                    ? "bg-green-100 text-green-800"
                    : service.status === "pending"
                    ? "bg-yellow-100 text-yellow-800"
                    : service.status === "rejected"
                    ? "bg-red-100 text-red-800"
                    : service.status === "suspended"
                    ? "bg-orange-100 text-orange-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {service.status === "approved" && "Approuvé"}
                {service.status === "pending" && "En attente"}
                {service.status === "rejected" && "Rejeté"}
                {service.status === "suspended" && "Suspendu"}
                {service.status === "closed" && "Fermé"}
              </span>
              {service.is_verified && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <ShieldCheckIcon className="w-3 h-3 mr-1" /> Vérifié
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="flex flex-wrap gap-2">
          {service.status === "pending" && (
            <>
              <button
                onClick={handleApprove}
                disabled={approveMutation.isPending}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <CheckCircleIcon className="w-4 h-4 mr-2" />
                Valider
              </button>
              <button
                onClick={() => setShowRejectModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <XCircleIcon className="w-4 h-4 mr-2" />
                Rejeter
              </button>
            </>
          )}

          {service.status === "approved" && (
            <button
              onClick={() => setShowSuspendModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              <PauseCircleIcon className="w-4 h-4 mr-2" />
              Suspendre
            </button>
          )}

          {(service.status === "suspended" ||
            service.status === "rejected") && (
            <button
              onClick={handleReactivate}
              disabled={reactivateMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlayCircleIcon className="w-4 h-4 mr-2" />
              Réactiver
            </button>
          )}

          <button
            onClick={() => toggleVerifiedMutation.mutate(id)}
            className={`inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              service.is_verified
                ? "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                : "border-transparent text-white bg-blue-500 hover:bg-blue-600"
            }`}
          >
            <ShieldCheckIcon className="w-4 h-4 mr-2" />
            {service.is_verified ? "Retirer vérif." : "Vérifier"}
          </button>
        </div>
      </div>

      {/* Message de rejet/suspension si présent */}
      {service.rejection_reason &&
        (service.status === "rejected" || service.status === "suspended") && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangleIcon
                  className="h-5 w-5 text-red-400"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Raison du{" "}
                  {service.status === "rejected" ? "rejet" : "blocage"}
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{service.rejection_reason}</p>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Toutes les valeurs, regroupées en deux cartes clé/valeur */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Service
          </h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field label="Titre" value={service.title} />
            <Field label="Catégorie" value={service.category} />
            <Field
              label="Prestations"
              value={service.services_offered?.join(", ")}
              full
            />
            <Field
              label="Tarif indicatif"
              value={formatPriceRange(service.price_range)}
            />
            <Field label="Zone d'intervention" value={service.service_zone} />
            <Field
              label="Rayon d'intervention"
              value={
                service.mobile_service_radius
                  ? `${service.mobile_service_radius} km`
                  : null
              }
            />
            <Field
              label="Disponible 24h/24"
              value={service.available_24_7 ? "Oui" : "Non"}
            />
            <Field
              label="Sur place"
              value={service.on_site_service ? "Oui" : "Non"}
            />
            <Field
              label="À domicile"
              value={service.mobile_service ? "Oui" : "Non"}
            />
            <Field
              label="Note"
              value={
                service.rating
                  ? `${service.rating} (${service.reviews_count || 0} avis)`
                  : null
              }
            />
            <Field label="Vues" value={service.views_count} />
            <Field label="Description" value={service.description} full stacked />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Contact & responsable
          </h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <div className="col-span-2">
              <PhoneField
                label="Téléphone"
                phone={service.service_phone}
                whatsapp={service.service_whatsapp}
              />
            </div>
            <Field label="Email" value={service.service_email} full />
            <Field label="Ville" value={service.city} />
            <Field label="Quartier" value={service.district} />
            <Field label="Adresse" value={service.address} full />
            <Field
              label="Localisation vérifiée"
              value={service.location_verified ? "Oui" : "Non"}
            />
            <Field
              label="Responsable"
              value={
                service.owner_firstname || service.owner_lastname
                  ? `${service.owner_firstname || ""} ${
                      service.owner_lastname || ""
                    }`.trim()
                  : null
              }
            />
            <Field label="Téléphone responsable" value={service.owner_phone} />
          </div>
        </div>
      </div>

      {/* Médias — galerie, vidéos, documents d'identité */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Galerie
          </h3>
          {!service.images || service.images.length === 0 ? (
            <div className="text-gray-500 italic flex items-center gap-2 text-sm">
              <ImageIcon className="w-4 h-4" /> Aucune image disponible
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {service.images.map((img, idx) => (
                <div
                  key={idx}
                  className="relative aspect-square group overflow-hidden rounded-lg bg-gray-100"
                >
                  <img
                    src={`${baseURL}/storage/${img}`}
                    alt={`Service image ${idx + 1}`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Vidéos
          </h3>
          {!service.videos_urls || service.videos_urls.length === 0 ? (
            <div className="text-gray-500 italic flex items-center gap-2 text-sm">
              <VideoIcon className="w-4 h-4" /> Aucune vidéo disponible
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {service.videos_urls.map((url, idx) => (
                <div key={idx} className="relative">
                  <video controls className="w-full rounded-lg bg-black" src={url} />
                  <button
                    type="button"
                    onClick={() => handleDeleteVideo(idx)}
                    disabled={deleteVideoMutation.isPending}
                    title="Supprimer cette vidéo"
                    className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-full p-1.5"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5 lg:col-span-2">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Documents (CNIB)
          </h3>
          {!service.cnib_recto && !service.cnib_verso ? (
            <p className="text-sm text-gray-500 italic">Aucun document fourni</p>
          ) : (
            <div className="grid grid-cols-2 gap-4 max-w-md">
              {service.cnib_recto && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Recto</p>
                  <img
                    src={`${baseURL}/storage/${service.cnib_recto}`}
                    alt="CNIB Recto"
                    className="w-full h-auto rounded object-cover max-h-40 cursor-pointer hover:opacity-90"
                    onClick={() =>
                      window.open(`${baseURL}/storage/${service.cnib_recto}`, "_blank")
                    }
                  />
                </div>
              )}
              {service.cnib_verso && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Verso</p>
                  <img
                    src={`${baseURL}/storage/${service.cnib_verso}`}
                    alt="CNIB Verso"
                    className="w-full h-auto rounded object-cover max-h-40 cursor-pointer hover:opacity-90"
                    onClick={() =>
                      window.open(`${baseURL}/storage/${service.cnib_verso}`, "_blank")
                    }
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modale de Rejet */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Rejeter le service
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Veuillez indiquer la raison du rejet. Cette information sera
              transmise au propriétaire.
            </p>
            <textarea
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              className="w-full border border-gray-300 rounded-md shadow-sm p-2 h-32 focus:ring-red-500 focus:border-red-500"
              placeholder="Raison du rejet..."
            />
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleReject}
                disabled={rejectMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {rejectMutation.isPending
                  ? "Traitement..."
                  : "Confirmer le rejet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale de Suspension */}
      {showSuspendModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Suspendre le service
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Veuillez indiquer la raison de la suspension. Le service ne sera
              plus visible.
            </p>
            <textarea
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              className="w-full border border-gray-300 rounded-md shadow-sm p-2 h-32 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Raison de la suspension..."
            />
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowSuspendModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSuspend}
                disabled={suspendMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50"
              >
                {suspendMutation.isPending
                  ? "Traitement..."
                  : "Confirmer la suspension"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDetails;
